import { CloseIcon, HamburgerMenuIcon } from "$components/icons";
import { Box, Drawer, IconButton, styled } from "@material-ui/core";
import React, { useState } from "react";

const Main = styled("main")(({ theme }) => ({
  position: "relative",
  display: "flex",
  backgroundColor: theme.palette.grey[100],
}));

const SideDrawer = styled(Drawer)(({ theme }) => ({
  "&.MuiDrawer-root": {
    width: "40vw",
    flexShrink: 0,
    height: "calc(100% - 64px)", // This matches the height of the app bar in the app layout
  },
  "& .MuiDrawer-paper": {
    bottom: 0,
    maxWidth: "85vw",
  },
  "& .MuiDrawer-paperAnchorDockedRight": {
    top: "64px", // This matches the height of the app bar in the app layout
    paddingBottom: "64px", // To counteract the `top` value, as it seems when the drawer is docked, using top blows the child out of the container.
    width: "40vw",
  },
}));

const ToggleDrawerButton = styled(IconButton)(({ theme }) => ({
  position: "fixed",
  top: `calc(64px + ${theme.spacing(0.5)})`,
  right: theme.spacing(4),
}));

/**
 * Should be used with the $components/layout/app-layout.tsx component.
 *
 * Provides a page layout with a right hand drawer that can be toggled open and
 * closed when on mobile, and is always open on desktop.
 */
export const PageWithRightHandDrawer: FCwC<
  { drawerContent: React.ReactNode } & React.ComponentProps<"main">
> = ({ children, drawerContent, ...restProps }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <Main {...restProps}>
      {children}
      <Box display={{ xs: "none", md: "block" }}>
        <SideDrawer variant="permanent" anchor="right" open>
          {drawerContent}
        </SideDrawer>
      </Box>
      <Box display={{ xs: "block", md: "none" }}>
        <SideDrawer
          variant="temporary"
          anchor="right"
          open={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen((curr) => !curr)}
          ModalProps={{
            keepMounted: true,
          }}
        >
          {drawerContent}
        </SideDrawer>
      </Box>
      <Box display={{ xs: "block", md: "none" }}>
        <ToggleDrawerButton
          edge="end"
          color="inherit"
          aria-label="open side menu"
          onClick={() => setIsMobileMenuOpen((curr) => !curr)}
        >
          {isMobileMenuOpen ? <CloseIcon /> : <HamburgerMenuIcon />}
        </ToggleDrawerButton>
      </Box>
    </Main>
  );
};
